import '../proposal-animations.css';

import { useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Archive,
  Calendar,
  CheckCircle,
  CheckDone01,
  ChevronDown,
  Inbox01,
  Mail01,
  Trash01,
  X,
  XCircle,
} from '@untitledui/icons';
import {
  type FC,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Key } from 'react-aria-components';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useAiActionsViewPref } from '@/hooks/useAiActionsSortPref';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useOnboardingStatusQuery } from '@/hooks/useOnboardingStatusQuery';
import { Button } from '@/lib/base/buttons/button';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { QueryErrorState } from '@/lib/components';
import type { AiActionsView } from '@/lib/localStorage/aiActionsSortPrefs';
import { useViewHealthMarker } from '@/lib/view-health';
import { cx } from '@/utils/cx';

import { useOrg } from '../../../contexts/OrgContext';
import { createProposalClient } from '../../../lib/proposals/core/ProposalClient';
import type {
  ProposalDetail,
  ProposalListItem,
  ProposalsListResponse,
} from '../../../lib/proposals/core/types';
import { useProposalActions } from '../../../lib/proposals/hooks/useProposalActions';
import {
  PROPOSALS_PAGE_SIZE,
  useMyPendingProposalsPaginated,
} from '../../../lib/proposals/hooks/useProposals';
import { proposalQueryKeys } from '../../../lib/proposals/query/keys';
import { useBulkMarkReadProposals } from '../../../lib/proposals/query/mutations';
import { proposalDetailQuery } from '../../../lib/proposals/query/queries';
import {
  addResolvingTombstones,
  clearResolvingTombstones,
} from '../../../lib/proposals/query/resolvingTombstones';
import {
  isOnboardingProposal,
  isWelcomeProposal,
} from '../../../lib/proposals/utils/onboardingConfig';
import { ActionRowItem } from '../ActionRowItem';
import { GeneratingActionsIndicator } from '../GeneratingActionsIndicator';
import { NAV_SHORTCUT_LABELS } from '../navShortcutLabels';
import { getGroupedLabels } from '../onboarding-step-labels';
import { OnboardingRetryNotice } from '../OnboardingRetryNotice';
import { ApproveAllControl } from '../ProposalListBulkActions';
import RecipientAvatar from '../RecipientAvatar';
import { ActionListSkeleton } from '../shared/ActionListSkeleton';
import { getOnboardingSortPriority } from '../shared/utils';
import { OnboardingActionCard } from './SidebarOnboardingActionCard';
import { OnboardingSectionHeader } from './SidebarOnboardingSectionHeader';
import { ProposalHistoryList } from './SidebarProposalHistoryList';

// Use shared constant to ensure cache sharing with other proposal hooks.
// 500 covers most users' pending inbox; virtualization handles display efficiently.
const PAGE_SIZE = PROPOSALS_PAGE_SIZE;

interface ActionListPanelProps {
  selectedActionId?: number;
  onActionSelect?: (actionId: number) => void;
  isAgentFeedSelected?: boolean;
  /** Called when the user switches view tabs (Open/Done/Dismissed/Archived).
      Not called for URL-driven syncs via viewOverride. */
  onViewChange?: (view: AiActionsView) => void;
  /** URL-derived view (from the /actions/{view} segment or the viewed action's
      resolution). When set and different from the current view, the panel
      adopts it. */
  viewOverride?: AiActionsView;
  /** Called with the first history row's id when a history view's first page
      loads (used to auto-select it when nothing is selected). */
  onFirstHistoryItem?: (id: number) => void;
}

export interface ProposalListPanelRef {
  removeAction: (actionId: number) => void;
  getNextAction: (currentActionId: number) => number | null;
  getAdjacentActionIds: (currentActionId: number) => {
    prev: number | null;
    next: number | null;
  };
}

// ── Priority grouping ───────────────────────────────────────────────

interface PriorityGroup {
  tier: PriorityTier;
  label: string;
  colorClass: string;
  actions: ProposalListItem[];
}

import AgentFeedRow from '../AgentFeedRow';
import type { PendingBulk } from '../list-panel/types';
import type { ApproveGroupRisk } from '../utils/approveGroupRisk';
import { detectApproveGroupRisk } from '../utils/approveGroupRisk';
import { getNextActionId } from '../utils/getNextActionId';
import type { PriorityTier } from '../utils/groupProposalsByTier';
import { groupProposalsByTier } from '../utils/groupProposalsByTier';
import { isCrmOnlyProposal } from '../utils/isCrmOnlyProposal';

// ── Virtual list row types ──────────────────────────────────────────
// The list is flattened into a heterogeneous array of rows so that
// @tanstack/react-virtual can virtualise the entire scrollable area
// (onboarding section, priority headers, action cards, indicators).

type VirtualRow =
  | { type: 'onboarding-header'; count: number }
  | { type: 'onboarding-action'; action: ProposalListItem; isSelected: boolean }
  | {
      type: 'generating-indicator';
      isActive: boolean;
      waitingForPipeline: boolean;
      messages: string[];
    }
  | {
      type: 'priority-header';
      group: PriorityGroup;
    }
  | {
      type: 'action';
      action: ProposalListItem;
      isSelected: boolean;
      isLastOfLastCategory: boolean;
      isCrmOnly: boolean;
      tier: PriorityTier;
    }
  | { type: 'bottom-padding' };

// Estimated pixel heights per row type — the virtualizer will measure
// actual heights after first render, but these keep the initial scroll
// bar thumb reasonably accurate.
const ROW_HEIGHT_ESTIMATES: Record<VirtualRow['type'], number> = {
  'onboarding-header': 44,
  'onboarding-action': 80,
  'generating-indicator': 48,
  'priority-header': 36,
  action: 78,
  'bottom-padding': 16,
};

const DEFAULT_GENERATING_MESSAGE = ['Setting things up…'];

/** Hoisted so `getNextAction` (which resolves nothing) reuses one instance. */
const NO_REMOVED_ACTION_IDS: ReadonlySet<number> = new Set<number>();

// GeneratingActionsIndicator used to be duplicated here, inline. It is the
// same component as ../GeneratingActionsIndicator — same three states, same
// copy, same spinner and width-measuring transition — which that file has
// since generalized behind an `inline` prop for the page-title row. The two
// had already drifted apart in the one way copies always do: this copy
// applied `px-4` only to the waiting state, the shared one applies it to all
// three. Harmless (the container is `justify-center` in a rail wider than its
// content), and worth losing to keep one implementation.

// ── Memoised subcomponents (defined at module scope to prevent recreation) ──

interface PriorityHeaderRowProps {
  group: PriorityGroup;
  approvingTier: string | null;
  /** True while any rows are selected — the whole-group controls hide
      (visibility, not unmount, so the header never reflows) because the
      SelectionToolbar's Approve/Dismiss own bulk actions in that mode;
      two near-identical stacked control rows invite scope mis-clicks. */
  selectionActive: boolean;
  onApprove: (group: PriorityGroup) => void;
  onDismiss: (group: PriorityGroup) => void;
}

// Group headers act on the WHOLE group with static labels — selection-scoped
// bulk actions live in the SelectionToolbar instead. Static labels keep the
// header from reflowing (and the group label from wrapping) as rows are
// selected/deselected.
const PriorityHeaderRow = memo(function PriorityHeaderRow({
  group,
  approvingTier,
  selectionActive,
  onApprove,
  onDismiss,
}: PriorityHeaderRowProps) {
  const handleApprove = useCallback(() => {
    onApprove(group);
  }, [onApprove, group]);

  const handleDismiss = useCallback(() => {
    onDismiss(group);
  }, [onDismiss, group]);

  return (
    // Band height mirrors the full list's header, measured in-browser at 32px
    // (`min-h-8`) — see DEFAULT_HEADER_HEIGHT in lib/base/grouped-list. A floor,
    // not a fixed height, so taller content can grow rather than clip.
    //
    // The label previously carried `leading-[28px]` on 12px text: a line box
    // more than twice the glyph height, which drove the band to a measured 45px.
    // The spacing was coming from the label's line-height rather than from the
    // band. Natural line-height plus a band floor puts it where it belongs.
    //
    // `uppercase` is a CSS transform, not baked into the string: an uppercase
    // literal breaks search, translation and screen readers (ahoy-ux-copy).
    <div className='flex min-h-8 items-center justify-between gap-2 px-4 border-t border-[var(--color-border-secondary)]'>
      <span
        className={`font-semibold text-[12px] tracking-wide whitespace-nowrap uppercase ${group.colorClass}`}
      >
        {group.label}
      </span>
      <div className={cx('shrink-0', selectionActive && 'invisible')}>
        <ApproveAllControl
          onApprove={handleApprove}
          onDismiss={handleDismiss}
          disabled={approvingTier !== null}
        />
      </div>
    </div>
  );
});

interface OnboardingActionRowProps {
  action: ProposalListItem;
  isSelected: boolean;
  isNew: boolean;
  showShortcuts: boolean;
  onMouseDown: (id: number) => void;
  onClick: (id: number) => void;
}

const OnboardingActionRow = memo(function OnboardingActionRow({
  action,
  isSelected,
  isNew,
  showShortcuts,
  onMouseDown,
  onClick,
}: OnboardingActionRowProps) {
  const handleMouseDown = useCallback(
    () => onMouseDown(action.id),
    [onMouseDown, action.id]
  );

  const handleClick = useCallback(
    () => onClick(action.id),
    [onClick, action.id]
  );

  return (
    <div className={`relative ${isNew ? 'proposal-enter' : ''}`}>
      {showShortcuts && isSelected && (
        <div className='absolute right-3 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-0.5 items-center'>
          <span className='text-[10px] font-medium text-fg-tertiary bg-bg-tertiary px-1.5 py-0.5 rounded'>
            {NAV_SHORTCUT_LABELS.up}
          </span>
          <span className='text-[10px] font-medium text-fg-tertiary bg-bg-tertiary px-1.5 py-0.5 rounded'>
            {NAV_SHORTCUT_LABELS.down}
          </span>
        </div>
      )}
      <OnboardingActionCard
        action={action}
        isSelected={isSelected}
        isNew={isNew}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      />
    </div>
  );
});

const VIEW_TABS: {
  id: AiActionsView;
  label: string;
  icon: FC<{ className?: string }>;
}[] = [
  { id: 'open', label: 'Open', icon: Inbox01 },
  { id: 'done', label: 'Done', icon: CheckCircle },
  { id: 'dismissed', label: 'Dismissed', icon: XCircle },
  // TODO(copy): 'Archived' is a banned state word — naming decision, needs Laura sign-off
  { id: 'archived', label: 'Archived', icon: Archive },
];
const RESOLUTION_FOR_VIEW: Record<
  Exclude<AiActionsView, 'open'>,
  'approved' | 'rejected' | 'withdrawn'
> = {
  done: 'approved',
  dismissed: 'rejected',
  archived: 'withdrawn',
};

const ActionListPanelComponent = forwardRef<
  ProposalListPanelRef,
  ActionListPanelProps
>(
  (
    {
      selectedActionId,
      onActionSelect,
      isAgentFeedSelected = false,
      onViewChange,
      viewOverride,
      onFirstHistoryItem,
    },
    ref
  ) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { currentOrg } = useOrg();
    const agentFeedEnabled = useFeatureFlag('agent_feed');
    const [view, setView] = useAiActionsViewPref();
    const handleViewChange = useCallback(
      (id: string) => {
        setView(id as AiActionsView);
        // Notify the parent so it updates the URL to /actions/{view} — this
        // also deselects the open detail (its id leaves the URL), since the
        // proposal being viewed doesn't belong to the newly selected segment.
        onViewChange?.(id as AiActionsView);
      },
      [setView, onViewChange]
    );
    // View-switcher dropdown wiring (menu-level onAction keeps item JSX free
    // of inline callbacks; selectedKeys marks the current view with a check).
    const handleViewMenuAction = useCallback(
      (key: Key) => {
        handleViewChange(String(key));
      },
      [handleViewChange]
    );
    const selectedViewKeys = useMemo(() => [view], [view]);
    const activeViewTab =
      VIEW_TABS.find(tab => tab.id === view) ?? VIEW_TABS[0];
    const activeViewLabel = activeViewTab.label;
    const ActiveViewIcon = activeViewTab.icon;
    // URL is the source of truth when it names a view (a /actions/{view}
    // segment, the resolution of the action named in the URL, or the bare
    // /actions Open default): adopt it. Layout effect (not useEffect) so the
    // persisted-preference view never paints for a frame before the override
    // is adopted — e.g. bare /actions with a persisted 'done' pref must
    // render Open on first paint, not flash the Done tab.
    useLayoutEffect(() => {
      if (viewOverride != null && viewOverride !== view) {
        setView(viewOverride);
      }
    }, [viewOverride, view, setView]);
    const isHistoryView = view !== 'open';
    const listContainerRef = useRef<HTMLDivElement>(null);
    const [approvingTier, setApprovingTier] = useState<string | null>(null);
    const [approvingId, setApprovingId] = useState<number | null>(null);
    // Track when modifier key (Cmd/Ctrl) is held for shortcut hints
    const [showShortcuts, setShowShortcuts] = useState(false);
    // Track selected proposal IDs for bulk approve
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    // State for risk-gated approval confirmation dialog
    const [pendingApprove, setPendingApprove] = useState<PendingBulk | null>(
      null
    );
    // State for the dismiss confirmation dialog (always gated by a modal)
    const [pendingDismiss, setPendingDismiss] = useState<PendingBulk | null>(
      null
    );
    // Shift+click selects the contiguous range between the last plain-clicked
    // row (the anchor) and the clicked row, applying the anchor click's
    // direction (select vs deselect) to the whole range — standard list
    // multi-select semantics. Plain clicks re-anchor; shift+clicks don't, so
    // successive shift+clicks re-range from the same anchor. Refs keep the
    // callback identity stable so every row doesn't re-render per toggle.
    const lastToggledRef = useRef<{ id: number; selected: boolean } | null>(
      null
    );
    const flattenedActionIdsRef = useRef<number[]>([]);
    const toggleSelected = useCallback((id: number, shiftKey?: boolean) => {
      const anchor = lastToggledRef.current;
      if (shiftKey && anchor && anchor.id !== id) {
        const order = flattenedActionIdsRef.current;
        const a = order.indexOf(anchor.id);
        const b = order.indexOf(id);
        if (a !== -1 && b !== -1) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          const range = order.slice(lo, hi + 1);
          setSelectedIds(prev => {
            const next = new Set(prev);
            for (const rangeId of range) {
              if (anchor.selected) {
                next.add(rangeId);
              } else {
                next.delete(rangeId);
              }
            }
            return next;
          });
          return;
        }
      }
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        // Idempotent under StrictMode's double-invoked updater: the value is
        // derived from `prev` each pass, so both passes write the same anchor.
        lastToggledRef.current = { id, selected: !prev.has(id) };
        return next;
      });
    }, []);
    const actions = useProposalActions(currentOrg?.id ?? 0);
    const bulkMarkReadMutation = useBulkMarkReadProposals(currentOrg?.id ?? 0);

    // Onboarding status — used to derive cycling messages from real step labels
    const { query: onboardingStatusQuery } = useOnboardingStatusQuery(
      currentOrg?.id
    );
    const onboardingStatus = onboardingStatusQuery.data;

    // Derive cycling messages from pending/in-progress onboarding steps.
    // Memoize on a stable string key (sorted step names) so the array identity
    // doesn't change on every React Query refetch when the data is the same.
    const inProgressKey = useMemo(() => {
      if (!onboardingStatus?.steps) {
        return '';
      }
      return onboardingStatus.steps
        .filter(s => s.status === 'in_progress')
        .map(s => s.step_name)
        .sort()
        .join(',');
    }, [onboardingStatus?.steps]);

    const onboardingMessages = useMemo(() => {
      if (!inProgressKey) {
        return DEFAULT_GENERATING_MESSAGE;
      }
      const names = inProgressKey.split(',');
      const pendingLabels = getGroupedLabels(names).map(label => `${label}…`);
      return pendingLabels.length > 0
        ? pendingLabels
        : DEFAULT_GENERATING_MESSAGE;
    }, [inProgressKey]);

    // The first-actions step parks in WAITING until the user creates a deal
    // pipeline. The generating-indicator renders this as a distinct informational
    // state rather than the "generating" spinner.
    const isWaitingForPipeline = useMemo(
      () =>
        (onboardingStatus?.steps ?? []).some(
          s => s.step_name === 'create_proposals' && s.status === 'waiting'
        ),
      [onboardingStatus?.steps]
    );

    // Note: there used to be a 5-second `invalidateQueries` setInterval here
    // that ran during onboarding to surface newly-created proposals. It has
    // been replaced by the proposal.created WebSocket push delivered to the
    // assignee — see lib/proposals/realtime/. If the WS push starts dropping
    // proposals (e.g., long disconnect during onboarding), bumping
    // `refetchOnReconnect` on the proposal list query is the right next step
    // before reaching for setInterval again.

    // Fetch all proposals in one query — groups handle display
    const pageSize = PAGE_SIZE;
    const {
      data: proposalsResponse,
      isLoading: loading,
      error,
      isFetching,
    } = useMyPendingProposalsPaginated(
      currentOrg?.id ?? 0,
      1,
      pageSize,
      undefined, // Always fetch all — groups handle display
      { enabled: !!currentOrg?.id }
    ) as {
      data: ProposalsListResponse | undefined;
      isLoading: boolean;
      isFetching: boolean;
      error: Error | null;
    };

    // View-health: fire `actions-list-items-rendered` post-commit once the
    // list query has resolved with ≥1 item. Fires against the current route
    // scope and satisfies part of the LOADED / PROPOSAL_LOADED variants for
    // `/:orgId/actions/*` (see dashboard.routes.tsx). On non-actions routes
    // where the sidebar may mount this panel, the marker is harmless — the
    // current scope's variants won't reference it.
    useViewHealthMarker('actions-list-items-rendered', {
      ready: (proposalsResponse?.results?.length ?? 0) > 0,
    });

    // Track which proposals we've already prefetched to avoid duplicate requests
    const prefetchedIdsRef = useRef<Set<number>>(new Set());

    // Track new actions for entrance animation.
    // useLayoutEffect fires synchronously before paint, so the animation
    // class is present on the first paint (no flash). State persists until
    // explicitly cleared, so re-renders don't strip the class mid-animation.
    const knownActionIdsRef = useRef<Set<number> | null>(null);
    const [newActionIds, setNewActionIds] = useState<Set<number>>(new Set());

    useLayoutEffect(() => {
      if (!proposalsResponse?.results) {
        return;
      }

      const currentIds = new Set(proposalsResponse.results.map(r => r.id));

      if (knownActionIdsRef.current === null) {
        // First load — seed known IDs, no animation
        knownActionIdsRef.current = currentIds;
        return;
      }

      const freshIds = new Set<number>();
      for (const id of currentIds) {
        if (!knownActionIdsRef.current.has(id)) {
          freshIds.add(id);
        }
      }

      knownActionIdsRef.current = currentIds;

      if (freshIds.size > 0) {
        setNewActionIds(freshIds);
      }
    }, [proposalsResponse?.results]);

    // Clear animation classes after the animation completes
    useEffect(() => {
      if (newActionIds.size === 0) {
        return;
      }
      const timer = setTimeout(() => {
        setNewActionIds(new Set());
      }, 800);
      return () => clearTimeout(timer);
    }, [newActionIds]);

    // Group non-onboarding proposals by priority tier — THE shared grouping
    // (groupProposalsByTier: newest-first within each tier), so the sidebar
    // renders identical ordering to the full-page AI Actions list. Color per
    // tier is a sidebar-only presentation concern layered on top. Neutral for
    // medium/CRM — color is reserved for HIGH so only genuine urgency shouts.
    const cachedPriorityGroupsRef = useRef<PriorityGroup[]>([]);
    const priorityGroups = useMemo<PriorityGroup[]>(() => {
      if (!proposalsResponse?.results) {
        return cachedPriorityGroupsRef.current;
      }

      const groups: PriorityGroup[] = groupProposalsByTier(
        proposalsResponse.results.filter(p => !isOnboardingProposal(p))
      ).map(group => ({
        ...group,
        colorClass:
          group.tier === 'high'
            ? 'text-[var(--color-error-600)]'
            : 'text-[var(--color-text-tertiary)]',
      }));

      cachedPriorityGroupsRef.current = groups;
      return groups;
    }, [proposalsResponse]);

    // Quick-approve a single CRM-only proposal from the list
    const handleQuickApprove = useCallback(
      async (proposalId: number) => {
        if (approvingId !== null) {
          return;
        }
        setApprovingId(proposalId);
        try {
          await actions.approve(proposalId);
          toast.success('Action approved');
        } catch (err) {
          console.error('[actions] Failed to quick-approve proposal', err, {
            proposalId,
            orgId: currentOrg?.id,
          });
          toast.error("We couldn't approve that action. Try again.");
        } finally {
          setApprovingId(null);
        }
      },
      [actions, approvingId, currentOrg]
    );

    // Approve OR dismiss a resolved set of proposals (a whole priority group,
    // or the cross-group selection — the caller decides the scope up front).
    // Both operations share identical optimistic-removal, partial-failure
    // restore, navigation, and cache-invalidation logic — only the per-proposal
    // client call and the user-facing verb differ.
    const executeBulkResolution = useCallback(
      async (
        tier: PriorityTier | 'selected',
        actionsToResolve: ProposalListItem[],
        mode: 'approve' | 'dismiss'
      ) => {
        if (approvingTier !== null) {
          return;
        } // Prevent double-clicks (shared guard across approve + dismiss)

        const op =
          mode === 'approve'
            ? {
                run: (
                  client: ReturnType<typeof createProposalClient>,
                  id: number
                ) => client.approve(id),
                verbPast: 'Approved',
              }
            : {
                run: (
                  client: ReturnType<typeof createProposalClient>,
                  id: number
                ) => client.reject(id),
                verbPast: 'Dismissed',
              };

        const toApprove = actionsToResolve;
        if (toApprove.length === 0) {
          return;
        }

        if (!currentOrg?.id) {
          return;
        }

        setApprovingTier(tier);

        const idsToRemove = new Set(toApprove.map(a => a.id));

        // Tombstone the ids for the resolve round-trip so an unrelated list
        // refetch (autosave onSuccess, WS repagination) can't resurrect the
        // optimistically-removed rows — same wiring as the full-page list's
        // useBulkResolution; see resolvingTombstones.ts.
        addResolvingTombstones(idsToRemove);

        // Optimistically remove ALL items from the list immediately
        if (currentOrg?.id) {
          queryClient.setQueriesData<ProposalsListResponse>(
            { queryKey: proposalQueryKeys.lists(currentOrg.id) },
            oldData => {
              if (!oldData?.results) {
                return oldData;
              }

              const removedActions = oldData.results.filter(a =>
                idsToRemove.has(a.id)
              );
              const newResults = oldData.results.filter(
                a => !idsToRemove.has(a.id)
              );

              // Update filter_tag_counts for all removed actions
              const newFilterTagCounts = { ...oldData.filter_tag_counts };
              for (const removed of removedActions) {
                if (removed.filter_tags) {
                  for (const tag of removed.filter_tags) {
                    if (newFilterTagCounts[tag] !== undefined) {
                      newFilterTagCounts[tag] = Math.max(
                        0,
                        (newFilterTagCounts[tag] ?? 0) - 1
                      );
                    }
                  }
                }
              }

              return {
                ...oldData,
                results: newResults,
                count: Math.max(
                  0,
                  (oldData.count ?? 0) - removedActions.length
                ),
                filter_tag_counts: newFilterTagCounts,
              };
            }
          );
        }

        // Clear removed items from selection immediately
        setSelectedIds(prev => {
          const next = new Set(prev);
          for (const id of idsToRemove) {
            next.delete(id);
          }
          return next;
        });

        // If the currently-viewed action is being resolved, advance to the
        // next action in flattened display order. The destination rule lives
        // in getNextActionId — the single definition shared with
        // ProposalListPanel and with this panel's own getNextAction ref
        // method below. Using remaining[0] here always jumped to the
        // top-priority item regardless of where the user was — "falling back
        // to the top of the list" — which this fix addresses (resolved
        // destination inconsistency, spec step 3).
        // flattenedActionIdsRef.current is used (not the memoised value)
        // because the memo is defined later in the component body and would
        // not be in scope here at parse/hook-call time.
        // window.innerWidth >= 1024 mirrors ProposalListPanel.tsx's guard: on
        // mobile the sidebar list isn't shown next to the detail view, so
        // advancing navigation there is meaningless — the row removal above
        // (which already ran) still happens below the threshold.
        if (
          selectedActionId &&
          idsToRemove.has(selectedActionId) &&
          window.innerWidth >= 1024
        ) {
          const nextActionId = getNextActionId(
            flattenedActionIdsRef.current,
            selectedActionId,
            idsToRemove
          );
          if (nextActionId !== null) {
            // Exactly one navigation owner: the parent (which navigates
            // with the view hint) when wired, else this panel. Navigating
            // in both places pushed duplicate history entries.
            if (onActionSelect) {
              onActionSelect(nextActionId);
            } else {
              // Bulk resolution only exists on the Open list — carry the
              // view so the tab doesn't flash while the next detail loads.
              navigate(`/${currentOrg.id}/actions/${nextActionId}`, {
                state: { view: 'open' },
              });
            }
          }
        }

        try {
          // Call the API client directly instead of going through the mutation
          // hook. Each mutation's onSuccess triggers invalidateQueries on the
          // list, causing a refetch that overwrites our optimistic removal with
          // server data that may still contain not-yet-processed items. By
          // calling the client directly we skip automatic cache invalidation
          // and do a single invalidation at the end.
          const client = createProposalClient(currentOrg.id);

          const results = await Promise.allSettled(
            toApprove.map(proposal => op.run(client, proposal.id))
          );

          let succeeded = 0;
          let failed = 0;
          const failedItems: ProposalListItem[] = [];

          results.forEach((result, idx) => {
            if (result.status === 'fulfilled') {
              succeeded++;
            } else {
              console.error(
                '[actions] Failed to approve proposal in bulk operation',
                { proposalId: toApprove[idx].id, orgId: currentOrg?.id },
                result.reason
              );
              failed++;
              failedItems.push(toApprove[idx]);
            }
          });

          // Re-add failed items back to the list
          if (failedItems.length > 0 && currentOrg?.id) {
            queryClient.setQueriesData<ProposalsListResponse>(
              { queryKey: proposalQueryKeys.lists(currentOrg.id) },
              oldData => {
                // The `results` guard also skips the history views' infinite
                // caches (InfiniteData under the same lists prefix) — writing
                // a flat `results` array into those would corrupt them.
                if (!oldData?.results) {
                  return oldData;
                }
                return {
                  ...oldData,
                  results: [...oldData.results, ...failedItems],
                  count: (oldData.count ?? 0) + failedItems.length,
                  filter_tag_counts: (() => {
                    const restored = { ...(oldData.filter_tag_counts ?? {}) };
                    for (const item of failedItems) {
                      if (item.filter_tags) {
                        for (const tag of item.filter_tags) {
                          // INCREMENT (not decrement) — we're restoring items, not removing them
                          restored[tag] = (restored[tag] ?? 0) + 1;
                        }
                      }
                    }
                    return restored;
                  })(),
                };
              }
            );

            console.warn('[actions] Bulk resolution partial failure', {
              mode,
              succeeded,
              failed,
              tier,
              orgId: currentOrg?.id,
            });
          }

          if (failed === 0) {
            toast.success(
              `${op.verbPast} ${succeeded} action${succeeded === 1 ? '' : 's'}`
            );
          } else {
            toast.error(
              `${op.verbPast} ${succeeded}. We couldn't process ${failed} of them.`
            );
          }

          // Mark list caches stale WITHOUT refetching. Approval submits in
          // the background (submitApprovalInBackground), so an eager
          // refetch here races the server-side commit: the response still
          // contains the just-approved proposal, resurrecting the
          // optimistically-removed row for a beat until the confirmation
          // path's invalidate removes it again (the
          // disappear→reappear→disappear flash). The confirmation paths
          // (submitApprovalInBackground / the reject mutation / WS proposal
          // events) all refresh the lists once the resolution has committed.
          if (currentOrg?.id) {
            queryClient.invalidateQueries({
              queryKey: proposalQueryKeys.lists(currentOrg.id),
              refetchType: 'none',
            });
          }
        } catch (e) {
          console.error(
            '[actions] Catastrophic failure in executeBulkResolution',
            { mode, tier, orgId: currentOrg?.id },
            e
          );
          // Mark list caches stale WITHOUT refetching — same race as the
          // success path above.
          if (currentOrg?.id) {
            queryClient.invalidateQueries({
              queryKey: proposalQueryKeys.lists(currentOrg.id),
              refetchType: 'none',
            });
          }
          const verb = mode === 'approve' ? 'approval' : 'dismissal';
          toast.error(`We couldn't complete the ${verb}. Try again.`);
        } finally {
          // Settled either way: fulfilled ids are gone server-side, failed
          // ids were restored above — stop suppressing both.
          clearResolvingTombstones(idsToRemove);
          setApprovingTier(null);
        }
      },
      [
        currentOrg,
        approvingTier,
        queryClient,
        selectedActionId,
        navigate,
        onActionSelect,
      ]
    );

    // The cross-group selection, as list items (for the SelectionToolbar ops)
    const selectedProposals = useMemo(
      () =>
        proposalsResponse?.results?.filter(p => selectedIds.has(p.id)) ?? [],
      [proposalsResponse?.results, selectedIds]
    );

    // Gate approval through a confirmation dialog when risky tags are present
    const requestApproveGroup = useCallback(
      (group: PriorityGroup) => {
        if (approvingTier !== null || group.actions.length === 0) {
          return;
        }
        const risk = detectApproveGroupRisk(group.actions);
        if (!risk.hasRisk) {
          void executeBulkResolution(group.tier, group.actions, 'approve');
          return;
        }
        setPendingApprove({
          tier: group.tier,
          actions: group.actions,
          buttonLabel: 'Approve all',
        });
      },
      [approvingTier, executeBulkResolution]
    );

    // Dismiss (reject) always opens a confirmation modal before resolving
    const requestDismissGroup = useCallback(
      (group: PriorityGroup) => {
        if (approvingTier !== null || group.actions.length === 0) {
          return;
        }
        setPendingDismiss({
          tier: group.tier,
          actions: group.actions,
          buttonLabel: 'Dismiss all',
        });
      },
      [approvingTier]
    );

    // Selection-toolbar counterparts: operate on the cross-group selection.
    const requestApproveSelected = useCallback(() => {
      if (approvingTier !== null || selectedProposals.length === 0) {
        return;
      }
      const risk = detectApproveGroupRisk(selectedProposals);
      if (!risk.hasRisk) {
        void executeBulkResolution('selected', selectedProposals, 'approve');
        return;
      }
      setPendingApprove({
        tier: 'selected',
        actions: selectedProposals,
        buttonLabel: 'Approve selected',
      });
    }, [approvingTier, selectedProposals, executeBulkResolution]);

    const requestDismissSelected = useCallback(() => {
      if (approvingTier !== null || selectedProposals.length === 0) {
        return;
      }
      setPendingDismiss({
        tier: 'selected',
        actions: selectedProposals,
        buttonLabel: 'Dismiss selected',
      });
    }, [approvingTier, selectedProposals]);

    const handleClearSelection = useCallback(() => {
      setSelectedIds(new Set());
    }, []);

    // Derived modal-display values — the pending state already carries its
    // resolved scope, so no re-deriving from selectedIds here.
    const pendingRisk = useMemo(
      (): ApproveGroupRisk | null =>
        pendingApprove ? detectApproveGroupRisk(pendingApprove.actions) : null,
      [pendingApprove]
    );

    const pendingCount = pendingApprove?.actions.length ?? 0;

    const handleConfirmApprove = useCallback(async () => {
      if (!pendingApprove) {
        return;
      }
      const pending = pendingApprove;
      setPendingApprove(null);
      await executeBulkResolution(pending.tier, pending.actions, 'approve');
    }, [pendingApprove, executeBulkResolution]);

    const handleCancelApprove = useCallback(() => {
      setPendingApprove(null);
    }, []);

    const dismissCount = pendingDismiss?.actions.length ?? 0;

    const handleConfirmDismiss = useCallback(async () => {
      if (!pendingDismiss) {
        return;
      }
      const pending = pendingDismiss;
      setPendingDismiss(null);
      await executeBulkResolution(pending.tier, pending.actions, 'dismiss');
    }, [pendingDismiss, executeBulkResolution]);

    const handleCancelDismiss = useCallback(() => {
      setPendingDismiss(null);
    }, []);

    const unreadCount = useMemo(() => {
      if (!proposalsResponse?.results) {
        return 0;
      }
      return proposalsResponse.results.filter(p => p.read_at === null).length;
    }, [proposalsResponse?.results]);

    const handleMarkAllAsRead = useCallback(async () => {
      if (bulkMarkReadMutation.isPending || unreadCount === 0) {
        return;
      }
      try {
        await bulkMarkReadMutation.mutateAsync({});
        toast.success('All actions marked as read');
      } catch (err) {
        console.error('[actions] Failed to mark all as read', err, {
          orgId: currentOrg?.id,
        });
        toast.error("We couldn't mark all as read. Try again.");
      }
    }, [bulkMarkReadMutation, unreadCount, currentOrg?.id]);

    const handleActionClick = useCallback(
      (actionId: number) => {
        if (currentOrg) {
          // Exactly one navigation owner: the parent (which navigates with
          // the view hint) when wired, else this panel. Navigating in both
          // places pushed duplicate history entries for the same URL.
          if (onActionSelect) {
            onActionSelect(actionId);
          } else {
            // Rows rendered by this panel are always Open-list rows (history
            // rows navigate via ProposalHistoryList) — carry the view so the
            // tab doesn't flash while the detail loads.
            navigate(`/${currentOrg.id}/actions/${actionId}`, {
              state: { view: 'open' },
            });
          }
        }
      },
      [navigate, currentOrg, onActionSelect]
    );

    const handleAgentFeedClick = useCallback(() => {
      if (currentOrg) {
        navigate(`/${currentOrg.id}/actions/agent-feed`);
      }
    }, [navigate, currentOrg]);

    // Prefetch proposal detail on mousedown to get ~100-200ms head start before click completes.
    // TanStack Query dedupes requests, so if prefetch is in-flight when click navigates,
    // the detail page's useQuery will reuse the same request.
    const handleActionMouseDown = useCallback(
      (actionId: number) => {
        if (currentOrg) {
          const cached = queryClient.getQueryData(
            proposalDetailQuery(currentOrg.id, actionId).queryKey
          );
          if (!cached) {
            queryClient.prefetchQuery(
              proposalDetailQuery(currentOrg.id, actionId)
            );
          }
        }
      },
      [currentOrg, queryClient]
    );

    // Track whether we've ever received data (survives refetches caused by switching
    // view segments or filters)
    // This prevents flashing back to skeleton during segment/filter-triggered refetches
    const hasEverLoadedRef = useRef(false);

    // Track the confirmed onboarding mode - only updated when we have FRESH data
    // This prevents oscillation when switching view segments/filters (different cache keys may have stale data)
    const confirmedOnboardingModeRef = useRef<boolean | null>(null);

    // Track if we're transitioning out of onboarding mode to prevent oscillation
    const isTransitioningFromOnboardingRef = useRef(false);

    // Backend onboarding count + the action-less welcome proposal, which carries
    // no 'onboarding' filter tag and so is absent from filter_tag_counts. Adding it
    // here lets onboarding mode (and the pinned section) engage even when the
    // welcome placeholder is the only onboarding item in the list.
    const onboardingCount =
      (proposalsResponse?.filter_tag_counts?.onboarding ?? 0) +
      ((proposalsResponse?.results ?? []).some(isWelcomeProposal) ? 1 : 0);

    // Only update refs when we have FRESH data (not stale data during refetch)
    // The key fix: check !isFetching to avoid updating with stale cached data
    // from different query keys (e.g., 'all' vs 'onboarding' filtered queries)
    if (proposalsResponse !== undefined && !isFetching) {
      hasEverLoadedRef.current = true;

      // Once we transition to non-onboarding mode, don't flip back
      // This prevents oscillation when 'all' query has stale cached data
      if (isTransitioningFromOnboardingRef.current) {
        // Stay in non-onboarding mode once we've transitioned
        if (onboardingCount === 0) {
          confirmedOnboardingModeRef.current = false;
          isTransitioningFromOnboardingRef.current = false;
        }
        // If onboarding count is > 0 during transition, ignore (stale data from other query)
      } else {
        const wasOnboarding = confirmedOnboardingModeRef.current === true;
        const wasNotOnboarding = confirmedOnboardingModeRef.current === false;
        const nowOnboarding = onboardingCount > 0;

        if (wasOnboarding && !nowOnboarding) {
          // Start transition - lock in non-onboarding mode
          isTransitioningFromOnboardingRef.current = true;
          confirmedOnboardingModeRef.current = false;
        } else if (wasNotOnboarding && nowOnboarding) {
          // New onboarding actions appeared - reset transition guard to allow onboarding mode
          isTransitioningFromOnboardingRef.current = false;
          confirmedOnboardingModeRef.current = true;
        } else {
          confirmedOnboardingModeRef.current = nowOnboarding;
        }
      }
    }

    // True initial loading = never received data before AND either actively loading
    // or query hasn't started yet (e.g., waiting for currentOrg to be available).
    // This prevents skeleton from reappearing during segment/filter-triggered refetches,
    // while also preventing a flash of empty state before the query starts.
    const isInitialLoading =
      !hasEverLoadedRef.current && (loading || !currentOrg?.id);

    // Use confirmed mode from ref (survives refetches where onboardingCount temporarily becomes 0)
    // Default to false only during true initial load
    // Note: isOnboardingMode no longer takes over the UI — it just controls whether
    // the onboarding section appears at the top and non-onboarding actions are disabled
    const isOnboardingMode = confirmedOnboardingModeRef.current ?? false;

    // Onboarding category data for the pinned section
    const onboardingCategory = useMemo(() => {
      const results = proposalsResponse?.results;
      if (!results) {
        return undefined;
      }
      const onboarding = results.filter(isOnboardingProposal);
      if (onboarding.length === 0) {
        return undefined;
      }
      const actions = [...onboarding].sort(
        (a, b) => getOnboardingSortPriority(a) - getOnboardingSortPriority(b)
      );
      return { actions };
    }, [proposalsResponse?.results]);

    // Show pinned onboarding section when in onboarding mode
    const showPinnedOnboarding = isOnboardingMode && onboardingCategory;

    // Onboarding pipeline is still running (steps not all complete)
    // Fall back to onboardingCount when status data is loading to prevent flash of empty state
    const isOnboardingActive =
      onboardingStatus != null
        ? !onboardingStatus.pipeline_complete
        : onboardingCount > 0;

    // ── Flatten into virtual rows ────────────────────────────────────
    const virtualRows = useMemo<VirtualRow[]>(() => {
      const rows: VirtualRow[] = [];

      // Pinned onboarding section
      if (showPinnedOnboarding && onboardingCategory) {
        rows.push({ type: 'onboarding-header', count: onboardingCount });
        for (const action of onboardingCategory.actions) {
          rows.push({
            type: 'onboarding-action',
            action,
            isSelected: selectedActionId === action.id,
          });
        }
        rows.push({
          type: 'generating-indicator',
          isActive: isOnboardingActive,
          waitingForPipeline: isWaitingForPipeline,
          messages: onboardingMessages,
        });
      }

      // Standalone onboarding progress when pipeline is active but no onboarding proposals
      if (isOnboardingActive && !showPinnedOnboarding) {
        rows.push({
          type: 'generating-indicator',
          isActive: true,
          waitingForPipeline: isWaitingForPipeline,
          messages: onboardingMessages,
        });
      }

      // Priority groups — always (the Date/Priority toggle was removed
      // 2026-09-02; ordering matches the full-page AI Actions list).
      const lastGroupIdx = priorityGroups.length - 1;
      priorityGroups.forEach((group, groupIdx) => {
        rows.push({ type: 'priority-header', group });

        group.actions.forEach((action, actionIdx) => {
          rows.push({
            type: 'action',
            action,
            isSelected: selectedActionId === action.id,
            isLastOfLastCategory:
              groupIdx === lastGroupIdx &&
              actionIdx === group.actions.length - 1,
            isCrmOnly: isCrmOnlyProposal(action),
            tier: group.tier,
          });
        });
      });

      // Bottom padding
      if (rows.length > 0) {
        rows.push({ type: 'bottom-padding' });
      }

      return rows;
    }, [
      showPinnedOnboarding,
      isOnboardingActive,
      isWaitingForPipeline,
      onboardingCategory,
      onboardingCount,
      selectedActionId,
      onboardingMessages,
      priorityGroups,
    ]);

    // ── Virtualizer ──────────────────────────────────────────────────
    const virtualizer = useVirtualizer({
      count: virtualRows.length,
      getScrollElement: () => listContainerRef.current,
      estimateSize: index => ROW_HEIGHT_ESTIMATES[virtualRows[index].type],
      overscan: 5, // render 5 extra items above/below viewport for smooth scrolling
    });

    // Keep the selected action in view even when its row is outside the
    // virtualizer's rendered window (e.g. ⌘↑ wrapping from the first action
    // to the last): an unmounted row has no DOM node, so the row-level
    // scrollIntoView can never fire — the virtualizer must own far jumps.
    // align:'auto' only scrolls when the row is out of view (mirroring the
    // row's block:'nearest'); once mounted, the row's own effect does the
    // precise final alignment. The ref gates on actual selection changes so
    // unrelated virtualRows updates (WS events, refetches) never yank the
    // user back to the selected row while they're browsing elsewhere.
    const lastScrolledToActionIdRef = useRef<number | null>(null);
    useEffect(() => {
      if (selectedActionId == null) {
        lastScrolledToActionIdRef.current = null;
        return;
      }
      if (lastScrolledToActionIdRef.current === selectedActionId) {
        return;
      }
      const index = virtualRows.findIndex(
        row =>
          (row.type === 'action' || row.type === 'onboarding-action') &&
          row.action.id === selectedActionId
      );
      // Only latch when the row exists so a selection that arrives before
      // its list data still scrolls once the rows land.
      if (index >= 0) {
        lastScrolledToActionIdRef.current = selectedActionId;
        virtualizer.scrollToIndex(index, { align: 'auto' });
      }
    }, [selectedActionId, virtualRows, virtualizer]);

    // Prefetch proposal details for visible items — enables instant navigation and
    // provides avatar data without expensive list serializer fields.
    // Uses a ref to track already-prefetched IDs to avoid duplicate requests.
    const visibleItems = virtualizer.getVirtualItems();
    useEffect(() => {
      if (!currentOrg) {
        return;
      }

      // Extract action IDs from visible virtual rows
      const visibleActionIds: number[] = [];
      for (const vItem of visibleItems) {
        const row = virtualRows[vItem.index];
        if (row.type === 'action' || row.type === 'onboarding-action') {
          visibleActionIds.push(row.action.id);
        }
      }

      // Prefetch any IDs we haven't prefetched yet
      const newPrefetches: number[] = [];
      for (const actionId of visibleActionIds) {
        if (!prefetchedIdsRef.current.has(actionId)) {
          prefetchedIdsRef.current.add(actionId);
          newPrefetches.push(actionId);
          queryClient.prefetchQuery(
            proposalDetailQuery(currentOrg.id, actionId)
          );
        }
      }
    }, [currentOrg, visibleItems, virtualRows, queryClient]);

    // Flattened list of action IDs in display order for next action navigation
    // Includes onboarding IDs first (from pinned section), then regular actions
    const flattenedActionIds = useMemo(() => {
      const ids: number[] = [];

      // Add onboarding actions first if pinned section is showing
      if (showPinnedOnboarding && onboardingCategory) {
        onboardingCategory.actions.forEach(action => ids.push(action.id));
      }

      priorityGroups.forEach(group => {
        group.actions.forEach(action => ids.push(action.id));
      });
      return ids;
    }, [priorityGroups, showPinnedOnboarding, onboardingCategory]);
    // Mirror for the stable toggleSelected callback (shift-range selection
    // needs the current visible order without re-creating the handler).
    flattenedActionIdsRef.current = flattenedActionIds;

    // Keyboard navigation: Cmd/Ctrl + Up/Down to navigate actions, track modifier key for hints
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        // Show shortcuts overlay when modifier is held
        if (e.metaKey || e.ctrlKey) {
          setShowShortcuts(true);
        }

        // Navigation: Cmd/Ctrl + Arrow Up/Down
        if (
          (e.metaKey || e.ctrlKey) &&
          (e.key === 'ArrowUp' || e.key === 'ArrowDown')
        ) {
          // Don't intercept native text-editing shortcuts in editable fields
          const target = e.target as HTMLElement | null;
          const isEditable =
            !!target &&
            (target.tagName === 'INPUT' ||
              target.tagName === 'TEXTAREA' ||
              target.isContentEditable);
          if (isEditable) {
            return;
          }
          e.preventDefault();

          if (flattenedActionIds.length === 0) {
            return;
          }

          const currentIndex = selectedActionId
            ? flattenedActionIds.indexOf(selectedActionId)
            : -1;

          let nextIndex: number;
          if (e.key === 'ArrowUp') {
            // Move up (previous action)
            if (currentIndex <= 0) {
              // At top or not found - wrap to bottom
              nextIndex = flattenedActionIds.length - 1;
            } else {
              nextIndex = currentIndex - 1;
            }
          } else {
            // Move down (next action)
            if (
              currentIndex === -1 ||
              currentIndex >= flattenedActionIds.length - 1
            ) {
              // Not found or at bottom - wrap to top
              nextIndex = 0;
            } else {
              nextIndex = currentIndex + 1;
            }
          }

          const nextActionId = flattenedActionIds[nextIndex];
          if (nextActionId !== undefined && currentOrg) {
            // Exactly one navigation owner: the parent (which navigates with
            // the view hint) when wired, else this panel.
            if (onActionSelect) {
              onActionSelect(nextActionId);
            } else {
              // Keyboard nav walks the Open list — carry the view so the tab
              // doesn't flash while the detail loads.
              navigate(`/${currentOrg.id}/actions/${nextActionId}`, {
                state: { view: 'open' },
              });
            }
          }
        }
      };

      const handleKeyUp = (e: KeyboardEvent) => {
        if (!e.metaKey && !e.ctrlKey) {
          setShowShortcuts(false);
        }
      };

      const handleBlur = () => setShowShortcuts(false);

      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);
      window.addEventListener('blur', handleBlur);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);
        window.removeEventListener('blur', handleBlur);
      };
    }, [
      flattenedActionIds,
      selectedActionId,
      currentOrg,
      navigate,
      onActionSelect,
    ]);

    // Expose removeAction and getNextAction methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        removeAction: (actionId: number) => {
          // Optimistically remove the action from the cached list
          // This provides immediate UI feedback before the backend confirms
          if (!currentOrg?.id) {
            return;
          }

          // Update all list queries for this org
          queryClient.setQueriesData<ProposalsListResponse>(
            { queryKey: proposalQueryKeys.lists(currentOrg.id) },
            oldData => {
              if (!oldData?.results) {
                return oldData;
              }

              // Filter out the removed action
              const newResults = oldData.results.filter(
                action => action.id !== actionId
              );

              // Update filter_tag_counts based on the removed action
              const removedAction = oldData.results.find(
                action => action.id === actionId
              );
              const newFilterTagCounts = { ...oldData.filter_tag_counts };
              if (removedAction?.filter_tags) {
                for (const tag of removedAction.filter_tags) {
                  if (newFilterTagCounts[tag] !== undefined) {
                    newFilterTagCounts[tag] = Math.max(
                      0,
                      (newFilterTagCounts[tag] ?? 0) - 1
                    );
                  }
                }
              }

              return {
                ...oldData,
                results: newResults,
                count: Math.max(0, (oldData.count ?? 0) - 1),
                filter_tag_counts: newFilterTagCounts,
              };
            }
          );
        },
        getNextAction: (currentActionId: number): number | null => {
          if (flattenedActionIds.indexOf(currentActionId) === -1) {
            // Action not in current view (edge case), return first action or null
            return flattenedActionIds[0] ?? null;
          }
          // Same destination rule as the bulk-resolution advance above and as
          // ProposalListPanel — one definition, in getNextActionId.
          return getNextActionId(
            flattenedActionIds,
            currentActionId,
            NO_REMOVED_ACTION_IDS
          );
        },
        getAdjacentActionIds: (
          currentActionId: number
        ): { prev: number | null; next: number | null } => {
          const currentIndex = flattenedActionIds.indexOf(currentActionId);
          if (currentIndex === -1) {
            return { prev: null, next: null };
          }
          return {
            prev:
              currentIndex > 0 ? flattenedActionIds[currentIndex - 1] : null,
            next:
              currentIndex < flattenedActionIds.length - 1
                ? flattenedActionIds[currentIndex + 1]
                : null,
          };
        },
      }),
      [flattenedActionIds, queryClient, currentOrg?.id]
    );

    const getSourceIcon = useCallback(
      (action: ProposalListItem) => {
        let proposal: ProposalDetail | ProposalListItem = action;
        if (currentOrg) {
          const cached = queryClient.getQueryData<ProposalDetail>(
            proposalDetailQuery(currentOrg.id, action.id).queryKey
          );
          if (cached) {
            proposal = cached;
          }
        }
        // Compact avatar matching checkbox size for sidebar
        return <RecipientAvatar action={proposal} size='xxs' />;
      },
      [currentOrg, queryClient]
    );

    if (error && !proposalsResponse?.results?.length) {
      return (
        <div className='flex justify-center items-center h-64'>
          <QueryErrorState error={error} label="We couldn't load proposals" />
        </div>
      );
    }

    const hasAnyContent = virtualRows.some(
      r => r.type === 'action' || r.type === 'onboarding-action'
    );
    const isShowingSkeleton =
      isInitialLoading ||
      (isFetching && !hasAnyContent && !showPinnedOnboarding);
    const isShowingEmptyState =
      !isShowingSkeleton &&
      !hasAnyContent &&
      !showPinnedOnboarding &&
      !isOnboardingActive &&
      hasEverLoadedRef.current;
    // Selection mode REPLACES the header row's content in place (same
    // wrapper, same min-height) rather than inserting a toolbar row —
    // selecting a row must never reflow the list below.
    const hasSelection = view === 'open' && selectedIds.size > 0;

    return (
      <div className='h-full flex flex-col bg-bg-primary'>
        {/* Header with title */}
        <div className='flex-shrink-0 px-[16px] pt-[16px] pb-[12px] bg-[var(--color-bg-secondary)]'>
          {/* min-h matches the sort radiogroup (28px: 18px line-height + 4px button
            padding + 4px group padding + 2px border) so the header keeps the same
            height on history tabs where the sort control is hidden. */}
          <div className='flex items-center gap-3 min-h-[28px]'>
            {hasSelection ? (
              <>
                <div className='flex items-center gap-1'>
                  <Button
                    color='tertiary'
                    size='sm'
                    onClick={handleClearSelection}
                    aria-label='Clear selection'
                    title='Clear selection'
                    iconLeading={
                      <X className='!size-3 !block' strokeWidth={2.5} />
                    }
                    className='!h-auto !rounded-[4px] !p-[4px] before:!hidden'
                  />
                  <span className='text-[12px] font-medium leading-[18px] whitespace-nowrap text-[var(--color-text-secondary)]'>
                    {selectedIds.size} selected
                  </span>
                </div>
                <div className='flex-1' />
                <div className='shrink-0'>
                  <ApproveAllControl
                    onApprove={requestApproveSelected}
                    onDismiss={requestDismissSelected}
                    disabled={approvingTier !== null}
                    label='Approve'
                    dismissLabel='Dismiss'
                  />
                </div>
              </>
            ) : (
              <>
                <Inbox01 className='w-[18px] h-[18px] shrink-0 text-[var(--color-text-secondary)]' />
                <h1 className='font-semibold text-[15px] leading-[20px] whitespace-nowrap shrink-0 text-[var(--color-text-primary)]'>
                  AI Actions
                </h1>
                {/* View switcher — a bare chevron beside the title (the current
                view is carried in the accessible name and shown checked in
                the menu). The menu holds all views with per-view icons. */}
                <Dropdown.Root instance='ai-actions-view'>
                  <Button
                    color='tertiary'
                    size='sm'
                    aria-label={`View: ${activeViewLabel}`}
                    className='!h-auto -ml-2 shrink-0 !rounded-md !p-[4px] before:!hidden'
                    iconLeading={
                      <ChevronDown
                        aria-hidden='true'
                        className='!size-4 !text-[var(--color-text-tertiary)]'
                        strokeWidth={2.25}
                      />
                    }
                  />
                  {/* 'bottom left' anchors the menu's left edge to the chevron —
                  the default 'bottom right' extends the 168px menu leftwards
                  from the tiny trigger, spilling over the nav sidebar. */}
                  <Dropdown.Popover
                    placement='bottom left'
                    className='min-w-[168px]'
                  >
                    <Dropdown.Menu
                      selectedKeys={selectedViewKeys}
                      onAction={handleViewMenuAction}
                    >
                      {VIEW_TABS.map(tab => (
                        <Dropdown.Item
                          key={tab.id}
                          id={tab.id}
                          label={tab.label}
                          icon={tab.icon}
                        />
                      ))}
                    </Dropdown.Menu>
                  </Dropdown.Popover>
                </Dropdown.Root>
                <div className='flex-1' />
                {/* History views hide the sort + mark-all controls, which would
                leave the header's right side empty AND (with the chevron-only
                switcher) no visible cue of the active view — so the current
                filter state renders here as a quiet chip. */}
                {view !== 'open' && (
                  /* Geometry mirrors the sort control that occupies this slot on
                 the Open tab: its 2px wrapper padding + 2px button padding +
                 18px line + border = 28px tall with a 10px side inset. */
                  <span className='flex items-center gap-1.5 shrink-0 rounded-md border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] px-[10px] py-[4px] text-[12px] font-medium leading-[18px] text-[var(--color-text-secondary)]'>
                    <ActiveViewIcon
                      aria-hidden='true'
                      className='size-3.5 text-[var(--color-text-tertiary)]'
                    />
                    {activeViewLabel}
                  </span>
                )}
                {/* Icon-only with tooltip: the text label wrapped to three lines
                at panel width once the view switcher joined the header row. */}
                {view === 'open' && unreadCount > 0 && (
                  <Tooltip title='Mark all as read' placement='bottom'>
                    <TooltipTrigger
                      aria-label='Mark all as read'
                      onPress={handleMarkAllAsRead}
                      isDisabled={bulkMarkReadMutation.isPending}
                      className='flex size-[26px] shrink-0 items-center justify-center rounded-md text-[var(--color-text-secondary)] transition-colors cursor-pointer hover:bg-[var(--color-bg-primary)] hover:text-[var(--color-text-primary)] disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      <CheckDone01 aria-hidden='true' className='size-4' />
                    </TooltipTrigger>
                  </Tooltip>
                )}
                {/* The Priority/Date sort toggle was removed (2026-09-02):
                    the sidebar always renders priority groups with
                    newest-first inside, matching the full-page list. */}
              </>
            )}
          </div>
        </div>

        {/* Actions List - virtualised scrollable area */}
        <div
          ref={listContainerRef}
          className='flex-1 overflow-y-auto overflow-x-hidden'
        >
          {isHistoryView && (
            <ProposalHistoryList
              orgId={currentOrg?.id ?? 0}
              resolution={
                RESOLUTION_FOR_VIEW[view as Exclude<AiActionsView, 'open'>]
              }
              selectedActionId={selectedActionId}
              onFirstItemLoaded={onFirstHistoryItem}
            />
          )}
          {view === 'open' &&
            currentOrg?.id != null &&
            onboardingStatus?.steps != null && (
              <OnboardingRetryNotice
                orgId={currentOrg.id}
                steps={onboardingStatus.steps}
              />
            )}
          {/* Show skeleton loading state for initial load or when fetching with no data */}
          {view === 'open' && isShowingSkeleton && <ActionListSkeleton />}
          {view === 'open' && isShowingEmptyState && (
            <div className='flex flex-col items-center py-12 px-4 w-full text-center'>
              {/* Anchor icon - nautical theme */}
              <svg
                className='w-20 h-20 mx-auto text-fg-quaternary mb-6'
                viewBox='0 0 64 64'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                {/* Ring / shackle at top */}
                <circle
                  cx='32'
                  cy='9'
                  r='4.5'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  fill='none'
                />
                {/* Vertical shank */}
                <line
                  x1='32'
                  y1='13.5'
                  x2='32'
                  y2='48'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                />
                {/* Stock (horizontal crossbar) */}
                <line
                  x1='22'
                  y1='20'
                  x2='42'
                  y2='20'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                />
                {/* Arms + crown: a single symmetric sweep (mirrored about x=32) */}
                <path
                  d='M14 37C14 49 22 52 32 52C42 52 50 49 50 37'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                  fill='none'
                />
                {/* Left fluke barb */}
                <path
                  d='M14 37L9 40M14 37L18 41'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  fill='none'
                />
                {/* Right fluke barb (mirror of left about x=32) */}
                <path
                  d='M50 37L55 40M50 37L46 41'
                  stroke='currentColor'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  fill='none'
                />
              </svg>
              <h3 className='text-lg font-medium text-[var(--color-text-primary)] mb-2'>
                Smooth sailing ahead
              </h3>
              <p className='text-secondary'>
                You're all caught up. No actions need your attention right now.
              </p>
            </div>
          )}
          {view === 'open' && !isShowingSkeleton && !isShowingEmptyState && (
            /* Virtualised list container — single div whose height matches
               the total measured size of all rows. Virtual items are
               absolutely positioned within it. */
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map(vItem => {
                const row = virtualRows[vItem.index];
                return (
                  <div
                    key={vItem.key}
                    ref={virtualizer.measureElement}
                    data-index={vItem.index}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${vItem.start}px)`,
                    }}
                  >
                    {row.type === 'onboarding-header' && (
                      <div role='region' aria-labelledby='onboarding-section'>
                        <OnboardingSectionHeader remainingCount={row.count} />
                      </div>
                    )}

                    {row.type === 'onboarding-action' && (
                      <OnboardingActionRow
                        action={row.action}
                        isSelected={row.isSelected}
                        isNew={newActionIds.has(row.action.id)}
                        showShortcuts={showShortcuts}
                        onMouseDown={handleActionMouseDown}
                        onClick={handleActionClick}
                      />
                    )}

                    {row.type === 'generating-indicator' && (
                      <GeneratingActionsIndicator
                        isActive={row.isActive}
                        waitingForPipeline={row.waitingForPipeline}
                        messages={row.messages}
                      />
                    )}

                    {row.type === 'priority-header' && (
                      <PriorityHeaderRow
                        group={row.group}
                        approvingTier={approvingTier}
                        selectionActive={selectedIds.size > 0}
                        onApprove={requestApproveGroup}
                        onDismiss={requestDismissGroup}
                      />
                    )}

                    {row.type === 'action' && (
                      <ActionRowItem
                        action={row.action}
                        isSelected={row.isSelected}
                        isChecked={selectedIds.has(row.action.id)}
                        isCrmOnly={row.isCrmOnly}
                        isNew={newActionIds.has(row.action.id)}
                        showShortcuts={showShortcuts}
                        isLastOfLastCategory={row.isLastOfLastCategory}
                        approvingId={approvingId}
                        onMouseDown={handleActionMouseDown}
                        onClick={handleActionClick}
                        onToggleSelected={toggleSelected}
                        onQuickApprove={handleQuickApprove}
                        getSourceIcon={getSourceIcon}
                      />
                    )}

                    {row.type === 'bottom-padding' && (
                      <div className='h-[16px]' />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Agent Feed — sticky footer, always visible at bottom of side panel */}
        {agentFeedEnabled && (
          <div className='flex-shrink-0'>
            <AgentFeedRow
              orgId={currentOrg?.id ?? null}
              isSelected={isAgentFeedSelected}
              onClick={handleAgentFeedClick}
            />
          </div>
        )}

        <ConfirmationDialog
          isOpen={pendingApprove !== null}
          title={`${pendingApprove?.tier === 'selected' ? 'Approve' : 'Approve all'} ${pendingCount} action${pendingCount === 1 ? '' : 's'}?`}
          description="Here's what these actions will do."
          confirmLabel={pendingApprove?.buttonLabel ?? 'Approve all'}
          cancelLabel='Cancel'
          variant={pendingRisk?.hasDestructive ? 'warning' : 'default'}
          instance='approve-group-confirm'
          onConfirm={handleConfirmApprove}
          onCancel={handleCancelApprove}
        >
          {pendingRisk && pendingRisk.hasRisk && (
            <div className='divide-y divide-[var(--color-border-secondary)] overflow-hidden rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)]'>
              {pendingRisk.emailCount > 0 && (
                <div className='flex items-center gap-3 px-3 py-2.5'>
                  <span className='flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-tertiary)] text-fg-secondary'>
                    <Mail01 className='h-4 w-4' />
                  </span>
                  <span className='text-sm text-fg-secondary'>
                    <span className='font-semibold text-[var(--color-text-primary)]'>
                      {pendingRisk.emailCount}
                    </span>{' '}
                    {pendingRisk.emailCount === 1
                      ? 'email will be sent'
                      : 'emails will be sent'}
                  </span>
                </div>
              )}
              {pendingRisk.calendarCount > 0 && (
                <div className='flex items-center gap-3 px-3 py-2.5'>
                  <span className='flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-tertiary)] text-fg-secondary'>
                    <Calendar className='h-4 w-4' />
                  </span>
                  <span className='text-sm text-fg-secondary'>
                    <span className='font-semibold text-[var(--color-text-primary)]'>
                      {pendingRisk.calendarCount}
                    </span>{' '}
                    {pendingRisk.calendarCount === 1
                      ? 'calendar invite will be sent'
                      : 'calendar invites will be sent'}
                  </span>
                </div>
              )}
              {pendingRisk.destructiveCount > 0 && (
                <div className='flex items-center gap-3 px-3 py-2.5'>
                  <span className='flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-warning-50 text-warning-600'>
                    <Trash01 className='h-4 w-4' />
                  </span>
                  <span className='text-sm text-fg-secondary'>
                    <span className='font-semibold text-[var(--color-text-primary)]'>
                      {pendingRisk.destructiveCount}
                    </span>{' '}
                    {pendingRisk.destructiveCount === 1
                      ? 'CRM record will be deleted or merged'
                      : 'CRM records will be deleted or merged'}
                  </span>
                </div>
              )}
            </div>
          )}
        </ConfirmationDialog>

        <ConfirmationDialog
          isOpen={pendingDismiss !== null}
          title={`${pendingDismiss?.tier === 'selected' ? 'Dismiss' : 'Dismiss all'} ${dismissCount} action${dismissCount === 1 ? '' : 's'}?`}
          description="Dismissed actions are cleared from your list and won't be acted on. The AI can suggest them again later."
          confirmLabel={pendingDismiss?.buttonLabel ?? 'Dismiss all'}
          cancelLabel='Cancel'
          variant='default'
          instance='dismiss-group-confirm'
          onConfirm={handleConfirmDismiss}
          onCancel={handleCancelDismiss}
        />
      </div>
    );
  }
);

ActionListPanelComponent.displayName = 'ActionListPanel';

// Wrap with memo to prevent unnecessary re-renders when parent state changes
// Use default shallow comparison for all props (selectedActionId, onActionSelect, isAgentFeedSelected)
const ActionListPanel = memo(ActionListPanelComponent);

export default ActionListPanel;
